// Import / export. JSON snapshots and CSV tables. No external APIs.

import { SCHEMA_VERSION, neutralSkeletonState } from './seed';
import { todayISO } from './dates';
import { migrateAction, newAction, cleanupExecutionQueue } from './actions';
import { seedWorkPackages, readyMovesFromPackages } from './workPackages';
import { CANONICAL_SPRINT_START, sprintInitialization } from './sprint';

// ------------------------------------------------------------------ CSV ----

export function toCSV(rows, columns) {
  const cols = columns || (rows.length ? Object.keys(rows[0]) : []);
  const esc = (v) => {
    if (v === null || v === undefined) return '';
    const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const head = cols.map(esc).join(',');
  const body = rows.map((r) => cols.map((c) => esc(r[c])).join(',')).join('\n');
  return rows.length ? `${head}\n${body}\n` : `${head}\n`;
}

// RFC4180-ish parser: handles quotes, embedded commas, newlines and CRLF.
export function parseCSV(text) {
  const src = String(text || '').replace(/^﻿/, '');
  const rows = [];
  let row = [];
  let field = '';
  let i = 0;
  let inQuotes = false;
  while (i < src.length) {
    const ch = src[i];
    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      field += ch;
      i += 1;
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      i += 1;
      continue;
    }
    if (ch === ',') {
      row.push(field);
      field = '';
      i += 1;
      continue;
    }
    if (ch === '\r') {
      i += 1;
      continue;
    }
    if (ch === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
      i += 1;
      continue;
    }
    field += ch;
    i += 1;
  }
  if (field.length || row.length) {
    row.push(field);
    rows.push(row);
  }
  const nonEmpty = rows.filter((r) => r.some((c) => String(c).trim() !== ''));
  if (!nonEmpty.length) return { headers: [], rows: [] };
  const headers = nonEmpty[0].map((h) => String(h).trim());
  const out = nonEmpty.slice(1).map((r) => {
    const o = {};
    headers.forEach((h, idx) => {
      o[h] = r[idx] === undefined ? '' : String(r[idx]).trim();
    });
    return o;
  });
  return { headers, rows: out };
}

// Maps loose header names onto canonical field keys.
export function mapHeaders(headers, aliases) {
  const norm = (s) => String(s).toLowerCase().replace(/[^a-z0-9]/g, '');
  const out = {};
  for (const h of headers) {
    const n = norm(h);
    for (const [key, list] of Object.entries(aliases)) {
      if (list.some((a) => norm(a) === n)) out[h] = key;
    }
  }
  return out;
}

export const STORY_ALIASES = {
  epicId: ['epic', 'epic id', 'epickey', 'parent', 'epic key'],
  id: ['id', 'key', 'issue key', 'story id'],
  title: ['title', 'summary', 'name', 'story'],
  status: ['status', 'state'],
  owner: ['owner', 'assignee'],
  start: ['start', 'start date'],
  end: ['end', 'due', 'due date', 'end date'],
  evidenceRequired: ['evidence', 'evidence required', 'expected evidence'],
  acceptance: ['acceptance', 'acceptance criteria', 'ac'],
  linked: ['linked', 'linked object', 'opportunity', 'deal'],
};

export const OPP_ALIASES = {
  account: ['account', 'company', 'name'],
  country: ['country', 'market'],
  customerType: ['customer type', 'type', 'segment'],
  existingPlatform: ['existing platform', 'incumbent', 'current platform'],
  technologyGap: ['technology gap', 'gap', 'problem'],
  blocksModule: ['blocks module', 'module', 'product'],
  decisionMaker: ['decision maker', 'contact', 'champion'],
  budget: ['budget'],
  deploymentTiming: ['deployment timing', 'timing', 'timeline'],
  implementationRequirement: ['implementation requirement', 'implementation'],
  recurringRevenue: ['recurring revenue', 'arr', 'mrr'],
  proposalValue: ['proposal value', 'value', 'deal value'],
  evidenceLevel: ['evidence level', 'evidence'],
  nextAction: ['next action', 'next step'],
  nextActionDue: ['date', 'next action date', 'due'],
  stage: ['stage'],
  tier: ['tier'],
};

// Revision 01 — founder-level pipeline imports. Deliberately small: Nexus
// remains the system of record. CSV is a fallback when CRM sync is unavailable.
export const ACCOUNT_ALIASES = {
  name: ['account name', 'account', 'name', 'company', 'organisation', 'organization'],
  country: ['country'],
  segment: ['segment', 'type', 'category'],
  tier: ['tier'],
  primaryContact: ['primary contact', 'contact'],
  whyBlocks: ['why blocks', 'why', 'rationale'],
  status: ['status', 'account status'],
  nextAction: ['next action', 'next step'],
  nextActionDue: ['next action date', 'date', 'due'],
  nexusId: ['nexus id', 'nexus reference id', 'nexus'],
  notes: ['notes', 'note'],
};

export const OPPORTUNITY_ALIASES = {
  name: ['opportunity name', 'opportunity', 'name'],
  accountName: ['account', 'account name', 'company'],
  stage: ['stage'],
  evidenceLevel: ['evidence level', 'evidence', 'e level'],
  assetName: ['asset', 'asset name'],
  nextAction: ['next action', 'next step'],
  nextActionDue: ['date', 'next action date', 'due'],
  nexusId: ['nexus id', 'nexus opportunity id', 'nexus'],
  type: ['type', 'opportunity type'],
  notes: ['notes', 'note'],
};

// Duplicate prevention (spec §31). Same account twice is a warning; the same
// account holding several opportunities is expected and allowed.
export function findDuplicateAccounts(existing, incoming) {
  const norm = (s) => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');
  const have = new Map((existing || []).map((a) => [norm(a.name), a]));
  const seen = new Set();
  const dupes = [];
  const fresh = [];
  for (const rec of incoming) {
    const k = norm(rec.name);
    if (!k) continue;
    if (have.has(k) || seen.has(k)) dupes.push(rec);
    else {
      seen.add(k);
      fresh.push(rec);
    }
  }
  return { fresh, dupes };
}

export const EXPENSE_ALIASES = {
  date: ['date'],
  label: ['label', 'description', 'item', 'vendor'],
  amount: ['amount', 'cost', 'value'],
  category: ['category', 'bucket'],
  linkedTo: ['linked', 'opportunity', 'deal'],
  evidenceObjective: ['evidence objective', 'evidence', 'objective'],
  reviewDate: ['review date', 'review'],
};

export function rowsToRecords(parsed, aliases, defaults = {}) {
  const map = mapHeaders(parsed.headers, aliases);
  return parsed.rows.map((r) => {
    const rec = { ...defaults };
    for (const [header, key] of Object.entries(map)) {
      if (r[header] !== undefined && r[header] !== '') rec[key] = r[header];
    }
    return rec;
  });
}

// ----------------------------------------------------------------- JSON ----

export function exportSnapshot(state) {
  return JSON.stringify(
    {
      ...stripSecrets(state),
      meta: { ...state.meta, exportedAt: new Date().toISOString(), schemaVersion: SCHEMA_VERSION },
    },
    null,
    2
  );
}

function stripSecrets(value) {
  if (Array.isArray(value)) return value.map(stripSecrets);
  if (value && typeof value === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      if (/^(apiKey|accessToken|secret|password)$/i.test(k)) continue;
      out[k] = stripSecrets(v);
    }
    return out;
  }
  return value;
}

// Deep-merge an imported snapshot onto a fresh seed so that missing keys from
// older versions never crash the app.
export function importSnapshot(text) {
  const parsed = JSON.parse(text);
  if (!parsed || typeof parsed !== 'object') throw new Error('Snapshot is not an object.');
  const base = neutralSkeletonState();
  return migrate(deepMerge(base, parsed));
}

export function deepMerge(base, patch) {
  if (Array.isArray(patch)) return patch;
  if (patch === null || patch === undefined) return base;
  if (typeof patch !== 'object') return patch;
  const out = Array.isArray(base) ? [...base] : { ...base };
  for (const [k, v] of Object.entries(patch)) {
    const b = out[k];
    if (v && typeof v === 'object' && !Array.isArray(v) && b && typeof b === 'object' && !Array.isArray(b)) {
      out[k] = deepMerge(b, v);
    } else {
      out[k] = v;
    }
  }
  return out;
}

export function migrate(state) {
  // Neutral skeleton only — never rehydrate Blocks DNA from seedState().
  let s = deepMerge(neutralSkeletonState(), state);
  const from = Number(state?.meta?.schemaVersion) || 1;
  if (from < 2) s = migrateToV2(s);
  if (from < 3) s = migrateToV3(s);
  if (from < 4) s = migrateToV4(s);
  if (from < 5) s = migrateToV5(s);
  if (from < 6) s = migrateToV6(s);
  if (from < 7) s = migrateToV7(s);
  s.meta = { ...s.meta, schemaVersion: SCHEMA_VERSION };
  return s;
}

// Revision 01. D001 used to be a standalone record. It is now the deep
// transaction page for whichever opportunity holds the Primary designation, so
// any existing D001 data becomes an Account + Opportunity + Asset Candidate.
// Nothing is destroyed: gates, partners, capital, constraint and evidence stay
// exactly where they are.
export function migrateToV2(state) {
  const s = { ...state };
  s.accounts = s.accounts || [];
  s.opportunities = s.opportunities || [];
  s.assetCandidates = s.assetCandidates || [];

  const legacy = s.d001 || {};
  const hasLegacy =
    legacy.exists ||
    String(legacy.sponsor?.company || '').trim() ||
    String(legacy.asset?.name || '').trim();

  if (!hasLegacy || s.opportunities.some((o) => o.primaryD001)) return s;

  const stamp = s.meta?.createdAt || s.settings?.sprintStart || todayISO();
  const sponsorName = String(legacy.sponsor?.company || '').trim() || 'Migrated D001 sponsor';
  const accountId = 'acc-migrated-d001';
  const oppId = 'opp-migrated-d001';
  const assetName = String(legacy.asset?.name || '').trim();

  s.accounts = [
    {
      id: accountId,
      name: sponsorName,
      market: 'Dubai',
      country: 'UAE',
      segment: 'Developer',
      tier: 'Tier A',
      status: 'Opportunity',
      whyBlocks: legacy.sponsor?.problem || '',
      relationshipPath: legacy.sponsor?.relationshipStrength || '',
      primaryContact: legacy.sponsor?.decisionMaker || '',
      decisionMakerKnown: !!legacy.sponsor?.decisionMaker,
      lastTouch: stamp,
      nextAction: legacy.constraint?.blocker || '',
      nextActionDue: legacy.constraint?.due || '',
      evidenceLevel: 'E0',
      nexusId: '',
      notes: 'Created automatically from the pre-revision D001 record.',
      stageSince: stamp,
      history: [{ stage: 'Opportunity', date: stamp }],
      problemConfirmed: legacy.sponsor?.problem || '',
    },
    ...s.accounts,
  ];

  s.opportunities = [
    {
      id: oppId,
      name: assetName ? `${sponsorName} — ${assetName}` : `${sponsorName} — first transaction`,
      accountId,
      accountName: sponsorName,
      market: 'Dubai',
      type: 'Dubai Founding Asset',
      sponsorProblem: legacy.sponsor?.problem || '',
      whyNow: legacy.sponsor?.whyNow || '',
      commercialObjective: legacy.sponsor?.commercialObjective || '',
      decisionMaker: legacy.sponsor?.decisionMaker || '',
      budgetSignal: '',
      timing: '',
      existingAlternative: legacy.sponsor?.currentAlternative || '',
      evidenceLevel: 'E0',
      stage: 'Primary D001',
      nextAction: legacy.constraint?.blocker || '',
      nextActionDue: legacy.constraint?.due || '',
      assetIdentified: !!assetName,
      d001Candidate: true,
      primaryD001: true,
      nexusId: '',
      notes: 'Migrated from the pre-revision D001 record.',
      scores: {},
      stageSince: stamp,
      lastTouch: stamp,
      history: [{ stage: 'Primary D001', date: stamp }],
    },
    ...s.opportunities,
  ];

  if (assetName) {
    s.assetCandidates = [
      {
        id: 'ast-migrated-d001',
        name: assetName,
        accountId,
        opportunityId: oppId,
        location: legacy.asset?.location || '',
        type: legacy.asset?.type || '',
        indicativeValue: legacy.asset?.indicativeValue || '',
        incomeProducing: legacy.asset?.income ? 'Yes' : 'Unknown',
        ownershipStatus: legacy.asset?.ownership || '',
        valuationStatus: legacy.asset?.valuation || '',
        documentsAvailable: legacy.asset?.documentsAvailable || '',
        sponsorAuthorityConfirmed: !!legacy.sponsor?.authorityConfirmed,
        commercialUrgency: '',
        regulatoryComplexity: '',
        capitalFit: '',
        repeatability: legacy.asset?.repeatPotential || '',
        screenStatus: legacy.asset?.passesScreen ? 'Passed' : 'In screen',
        notes: legacy.asset?.thesis || '',
      },
      ...s.assetCandidates,
    ];
  }

  s.d001 = { ...legacy, exists: true, primaryOpportunityId: oppId };
  return s;
}

export function migrateToV3(state) {
  const s = { ...state };
  s.top3 = (s.top3 || []).map(migrateAction);
  return s;
}

export function migrateToV4(state) {
  const s = { ...state };
  s.workPackages = s.workPackages?.length ? s.workPackages : seedWorkPackages();
  s.recommendationDecisions = s.recommendationDecisions || [];
  s.top3 = (s.top3 || []).map(migrateAction);
  const generated = readyMovesFromPackages(
    s.workPackages.filter((p) => p.setup),
    s.top3,
    s.settings?.sprintStart || todayISO(),
  );
  for (const m of generated) {
    s.top3.push(newAction({ ...m, id: `wp-move-${m.workPackageId}` }, m.dueDate));
  }
  const waiting = (obj) => {
    if (!obj || typeof obj !== 'object') return obj;
    if (obj.waitingExternal === undefined) {
      obj.waitingExternal = false;
      obj.waitingUntil = obj.waitingUntil || '';
      obj.waitingFor = obj.waitingFor || '';
    }
    return obj;
  };
  s.opportunities = (s.opportunities || []).map((o) => waiting({ ...o }));
  s.assetCandidates = (s.assetCandidates || []).map((o) => waiting({ ...o }));
  if (s.d001?.partners) s.d001 = { ...s.d001, partners: s.d001.partners.map((p) => waiting({ ...p })) };
  if (s.infra?.opportunities) {
    s.infra = { ...s.infra, opportunities: s.infra.opportunities.map((o) => waiting({ ...o })) };
  }
  return s;
}

export function migrateToV5(state) {
  const s = { ...state };
  const init = sprintInitialization({
    ...(s.settings?.sprint || {}),
    start: CANONICAL_SPRINT_START,
    initializedAt: s.settings?.sprint?.initializedAt || CANONICAL_SPRINT_START,
    initializedBy: s.settings?.sprint?.initializedBy || 'system:migrate',
    kind: s.settings?.sprint?.kind === 'rebaseline' ? 'rebaseline' : 'initialization',
  });
  s.settings = {
    ...(s.settings || {}),
    sprintStart: init.start,
    sprint: init,
  };
  return s;
}

export function migrateToV6(state) {
  const s = { ...state };
  const today = s.settings?.sprint?.start || s.settings?.sprintStart || todayISO();
  s.top3 = cleanupExecutionQueue((s.top3 || []).map(migrateAction), today);
  s.workPackages = (s.workPackages || []).map((p) => {
    if (p.id === 'wp-clock' && p.status !== 'Done') return { ...p, status: 'Done' };
    if (p.id === 'wp-universe') return { ...p, founderMove: false };
    if (p.id === 'wp-scope-cleanup') return { ...p, founderMove: false };
    return p;
  });
  return s;
}

/** Re-run queue cleanup so "Build the Dubai target universe…" leaves Today's AI Plan. */
export function migrateToV7(state) {
  const s = { ...state };
  const today = s.settings?.sprint?.start || s.settings?.sprintStart || todayISO();
  s.top3 = cleanupExecutionQueue((s.top3 || []).map(migrateAction), today);
  return s;
}

// -------------------------------------------------------------- browser ----

export function download(filename, text, mime = 'text/plain;charset=utf-8') {
  if (typeof document === 'undefined') return;
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const CSV_TABLES = {
  accounts: {
    label: 'Dubai accounts',
    columns: [
      'id', 'name', 'country', 'segment', 'tier', 'status', 'primaryContact', 'decisionMakerKnown',
      'whyBlocks', 'relationshipPath', 'lastTouch', 'nextAction', 'nextActionDue', 'evidenceLevel', 'nexusId', 'notes',
    ],
    get: (s) => s.accounts || [],
  },
  opportunities: {
    label: 'Dubai opportunities',
    columns: [
      'id', 'name', 'accountName', 'type', 'stage', 'evidenceLevel', 'sponsorProblem', 'whyNow',
      'commercialObjective', 'decisionMaker', 'budgetSignal', 'timing', 'existingAlternative',
      'assetIdentified', 'd001Candidate', 'primaryD001', 'nextAction', 'nextActionDue', 'nexusId',
    ],
    get: (s) => s.opportunities || [],
  },
  assets: {
    label: 'Asset candidates',
    columns: [
      'id', 'name', 'accountId', 'opportunityId', 'location', 'type', 'indicativeValue', 'incomeProducing',
      'ownershipStatus', 'valuationStatus', 'documentsAvailable', 'sponsorAuthorityConfirmed',
      'commercialUrgency', 'regulatoryComplexity', 'capitalFit', 'repeatability', 'screenStatus',
    ],
    get: (s) => s.assetCandidates || [],
  },
  evidence: {
    label: 'Evidence',
    columns: ['id', 'date', 'level', 'market', 'title', 'counterparty', 'description', 'linkedTo'],
    get: (s) => s.evidence || [],
  },
  expenses: {
    label: 'Expenses',
    columns: ['id', 'date', 'label', 'amount', 'category', 'linkedTo', 'evidenceObjective', 'reviewDate'],
    get: (s) => s.expenses || [],
  },
  infrastructure: {
    label: 'Infrastructure opportunities',
    columns: [
      'id', 'account', 'country', 'customerType', 'tier', 'stage', 'existingPlatform', 'technologyGap',
      'blocksModule', 'decisionMaker', 'budget', 'deploymentTiming', 'implementationRequirement',
      'recurringRevenue', 'proposalValue', 'evidenceLevel', 'nextAction', 'nextActionDue', 'lastTouch',
    ],
    get: (s) => s.infra?.opportunities || [],
  },
  partners: {
    label: 'Execution partners',
    columns: [
      'id', 'org', 'stage', 'permission', 'activities', 'limitations', 'clientOwnership', 'dataOwnership',
      'integration', 'economics', 'sla', 'nextAction', 'evidenceLevel',
    ],
    get: (s) => s.d001?.partners || [],
  },
  stories: {
    label: '45-day plan stories',
    columns: ['epicId', 'id', 'title', 'status', 'owner', 'start', 'end', 'evidenceRequired', 'acceptance', 'linked'],
    get: (s) => (s.plan?.epics || []).flatMap((e) => (e.stories || []).map((st) => ({ ...st, epicId: e.id }))),
  },
  decisions: {
    label: 'Decision log',
    columns: ['id', 'date', 'type', 'question', 'options', 'evidence', 'decision', 'reviewDate'],
    get: (s) => s.decisions || [],
  },
  top3: {
    label: 'Founder actions',
    columns: [
      'id', 'date', 'text', 'workstream', 'priority', 'status', 'expectedEvidence', 'dueDate', 'owner',
      'gateRelevance', 'linkedKind', 'linkedId', 'linkedLabel', 'blocker', 'selectedForToday',
      'completionResult', 'evidenceCreated', 'nextActionText', 'outcome', 'outcomeNote',
    ],
    get: (s) => s.top3 || [],
  },
  reviews: {
    label: 'Weekly reviews',
    columns: [
      'weekStart', 'money', 'asset', 'regulatedExecution', 'institutionalAccess', 'capital', 'funding',
      'notWorking', 'wasted', 'continue', 'stop', 'concentrate', 'constraint', 'nextObjective',
      'leak', 'leakNote', 'survives', 'survivesNote',
    ],
    get: (s) => s.reviews || [],
  },
  sprintReviews: {
    label: 'Sprint post-mortems',
    columns: [
      'id', 'sprintStart', 'sprintEnd', 'endedAt', 'endedBy', 'earlyClose', 'option',
      'proved', 'disproved', 'stop', 'concentrate', 'biggestMiss', 'rationale',
      'carryForward', 'runAgain', 'runAgainWhy', 'decisionId',
    ],
    get: (s) => s.sprintReviews || [],
  },
  saudi: {
    label: 'Saudi opportunities',
    columns: ['id', 'account', 'offer', 'customerType', 'stage', 'evidenceLevel', 'nextAction', 'nextActionDue'],
    get: (s) => s.saudi?.opportunities || [],
  },
};
